import React from 'react';
import { TextField, ActionButton, Link } from '@fluentui/react';
import ImageUploading from "react-images-uploading";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Editor from '@monaco-editor/react';
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getEntityType, MarketplaceOffer } from '@the-real-insight/tri-model';
import { userAPI } from '../UserAPI';
import { useModalContext } from '../components/ModalProvider';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';

interface MarketingDetailsProps {
    type: any
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
    onImagesChange: (imageList: any, addUpdateIndex: any) => void;
}

export default function MarketingDetails({
    type,
    marketplaceOffer,
    setMarketplaceOffer,
    onImagesChange,
}: MarketingDetailsProps) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const editorOptions = {
        fontSize: 12,
        minimap: { enabled: false },
        scrollbar: {
            vertical: 'hidden' as const,
            horizontal: 'hidden' as const
        },
        lineNumbers: 'off' as const,
        fontFamily: "monospace"
    };

    return (
        <div>
            <div className="displayFlex gapM">
                <div>
                    <TextField
                        label="URL Allgemeine Geschäftsbedingungen"
                        value={marketplaceOffer.urlTermsAndConditions}
                        onChange={(e, value) => {
                            setMarketplaceOffer(new (getEntityType(type.name).type.type)({
                                ...marketplaceOffer,
                                urlTermsAndConditions: value
                            }));
                        }}
                        styles={{ root: { width: 400 } }}
                    />
                    <TextField
                        label="URL Datenschutzbestimmungen"
                        value={marketplaceOffer.urlPrivacyPolicy}
                        onChange={(e, value) => {
                            setMarketplaceOffer(new (getEntityType(type.name).type.type)({
                                ...marketplaceOffer,
                                urlPrivacyPolicy: value
                            }));
                        }}
                        styles={{ root: { width: 400 } }}
                    />
                </div>
                <div>
                    <div className="marginBottomM textHeading">Bilder für Marktplatz</div>
                    <ImageUploading
                        value={marketplaceOffer.images}
                        onChange={onImagesChange}
                        maxNumber={3}
                        maxFileSize={8000000}
                    >
                        {({
                            imageList,
                            onImageUpload,
                            onImageRemoveAll,
                            onImageUpdate,
                            onImageRemove,
                            isDragging,
                            dragProps,
                        }) => (
                            <div className="displayFlex gapM">
                                <div className="width150 height100 displayFlex flexDirectionColumn alignItemsCenter justifyContentCenter borderNeutralDashed backgroundColorPrimaryLightest"
                                    style={isDragging ? { borderColor: "red" } : undefined}
                                    {...dragProps}>
                                    <ActionButton iconProps={{ iconName: 'Photo2Add' }} onClick={onImageUpload}>
                                        Datei hochladen
                                    </ActionButton>
                                </div>
                                {marketplaceOffer.images ? marketplaceOffer.images.map((uri: string, index: number) => (
                                    <div key={index} className="displayFlex flexDirectionColumn alignItemsCenter justifyContentCenter">
                                        <Zoom>
                                            <div role="img" className="width150 height100 borderNeutral"
                                                style={{ width: 150, height: 100, backgroundImage: `url(${userAPI.getResourceURL(uri)})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }} />
                                        </Zoom>
                                        <ActionButton iconProps={{ iconName: 'Delete' }} onClick={async () => {
                                            try {
                                                const updatedMarketplaceOffer = new (getEntityType(type.name).type.type)({ ...marketplaceOffer });
                                                updatedMarketplaceOffer.images = updatedMarketplaceOffer.images.filter((_: any, i: number) => i !== index);
                                                const result = await userAPI.updateMarketplaceOffer(authentication.customer, updatedMarketplaceOffer);
                                                if (result) {
                                                    setMarketplaceOffer(result);
                                                }
                                            } catch (error: any) {
                                                pushError(error);
                                            }
                                        }}>
                                            Delete
                                        </ActionButton>
                                    </div>
                                )) : <></>}
                            </div>
                        )}
                    </ImageUploading>
                </div>
            </div>
            <div className="marginTopM textHeading">Marketingtext für den Marktplatz</div>
            <div className="marginTopS textM">Eingabe als <Link>Markdown</Link>. Dieser Text wird für den <a href="https://www.real-gain.com/" target="_blank">REAL GAIN Marktplatz</a> verwendet.</div>
            <div className="marginTopM displayFlex gapM">
                <div className="width600 borderNeutral">
                    <Editor height="200px" defaultLanguage="markdown"
                        options={editorOptions}
                        value={marketplaceOffer.marketingContent}
                        onChange={(value) => {
                            setMarketplaceOffer(new (getEntityType(type.name).type.type)({
                                ...marketplaceOffer,
                                marketingContent: value
                            }));
                        }} />
                </div>
                <div className="width500 height200 overflowYScroll backgroundColorWhite borderNeutral paddingM">
                    <Markdown remarkPlugins={[remarkGfm]}>{marketplaceOffer.marketingContent}</Markdown>
                </div>
            </div>
        </div>
    );
} 